import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import capitalize from 'lodash/capitalize';
import { subDays, subMonths, subYears, isBefore } from 'date-fns';
import {
  DateForm,
  DateFilter,
  DateRangeOption,
  DateRangeOptionType,
} from '@cigna/chcp/shared/util';

export const rangeOptions: DateRangeOption[] = [
  {
    value: 'all',
    description: 'All',
  },
  {
    value: '-24h',
    description: 'Past 24 hours',
  },
  {
    value: '-7d',
    description: 'Past 7 days',
  },
  {
    value: '-30d',
    description: 'Past 30 days',
  },
  {
    value: '-60d',
    description: 'Past 60 days',
  },
  {
    value: '-6m',
    description: 'Past 6 months',
  },
  {
    value: 'editable',
    description: 'Custom date range',
  },
];

@Component({
  selector: 'cigna-filter-type-date-shared',
  templateUrl: './filter-type-date.component.html',
  styleUrls: ['./filter-type-date.component.scss'],
})
export class FilterTypeDateSharedComponent implements OnInit, OnDestroy {
  @Input() label: string;
  @Input() isExpanded = false;
  @Input() set filterState(_filterState: DateFilter) {
    this._filterState = _filterState;
    this.dateForm.patchValue(
      {
        ..._filterState,
        option: _filterState.options?.find((v) => v.isSelected)?.value,
      },
      { emitEvent: false },
    );
    this.isDateReadOnly = _filterState.range !== 'editable';
  }
  @Input() customRanges: DateRangeOptionType[];

  @Output() openFilters = new EventEmitter();
  @Output() filterChanged: EventEmitter<DateFilter> = new EventEmitter();
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  _filterState: DateFilter;
  onDestroy$ = new Subject();

  rangeOptions = rangeOptions;

  isDateReadOnly = true;
  minFromDate = subYears(new Date(), 2);
  maxDate = new Date();
  dateFormat = 'MM/dd/yyyy';

  dateForm = this.formBuilder.group(
    {
      option: [''],
      range: ['all'],
      min: [''],
      max: [''],
    },
    {
      validators: [
        (group: FormGroup): ValidationErrors | null =>
          (group.controls.max.value || group.controls.min.value) &&
          isBefore(group.controls.max.value, group.controls.min.value)
            ? { isValidDateRange: true }
            : null,
      ],
    },
  );

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.rangeOptions =
      (this.customRanges &&
        rangeOptions.filter((v) => this.customRanges.includes(v.value))) ||
      rangeOptions;
    this.dateForm.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue: DateForm) => {
        const options = this._filterState.options?.map((v) => ({
          ...v,
          isSelected: v.value === formValue.option,
        }));
        if (formValue.max) {
          formValue.max?.setHours(23, 59, 59);
        }
        this.filterChanged.emit({ ...formValue, options });
      });

    this.dateForm.statusChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isValid) => {
        this.isValid.emit(isValid === 'VALID');
      });

    this.range.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((range) => {
        this.isDateReadOnly = true;
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        switch (range) {
          case 'all': {
            this.min.setValue(undefined);
            this.max.setValue(undefined);
            break;
          }
          case '-24h': {
            this.min.setValue(subDays(new Date(), 1));
            this.max.setValue(new Date());
            break;
          }
          case '-7d': {
            this.min.setValue(subDays(todayMidnight, 7));
            this.max.setValue(new Date());
            break;
          }
          case '-30d': {
            this.min.setValue(subDays(todayMidnight, 30));
            this.max.setValue(new Date());
            break;
          }
          case '-60d': {
            this.min.setValue(subDays(todayMidnight, 60));
            this.max.setValue(new Date());
            break;
          }
          case '-6m': {
            this.min.setValue(subMonths(todayMidnight, 6));
            this.max.setValue(new Date());
            break;
          }
          case 'editable': {
            this.isDateReadOnly = false;
            break;
          }
          default:
            break;
        }
      });
  }

  get customLabel(): string {
    return `${this.option.value ? `${capitalize(this.option.value)} - ` : ''}${
      rangeOptions.find((d) => d.value === this.range.value)?.description
    }`;
  }

  get option(): FormControl {
    return this.dateForm.controls.option as FormControl;
  }
  get range(): FormControl {
    return this.dateForm.controls.range as FormControl;
  }
  get min(): FormControl {
    return this.dateForm.controls.min as FormControl;
  }
  get max(): FormControl {
    return this.dateForm.controls.max as FormControl;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
